import {
    FareFamilyFareRule,
    FareFamilyV3OnewaySolution,
    FareFamilyV3Response,
    SpclFareFamilyFareRule
} from '@/src/components/desktop/srp/fareFamily/types'

/** Leg key in a SPECIAL rule's per-leg maps (mirrors the v3 flightParam index): 1 = onward, 2 = return. */
type SpclLegKey = '1' | '2'

/**
 * Case-insensitive "is this a pure-Regular brand?".
 * Accepts both the per-leg form ("Regular") and the combo form ("Regular_Regular") — the special RT
 * combo only exists for Regular + Regular, and BE has returned it under either spelling.
 */
export const isRegularComboBrand = (brand?: string | null): boolean => {
    const parts = (brand ?? '')
        .trim()
        .toLowerCase()
        .split('_')
        .filter(Boolean)
    return parts.length > 0 && parts.every(part => part === 'regular')
}

/**
 * Identify the two ONEWAY leg solutions in a v3 response.
 *
 * Resolved STRICTLY by the `index` BE echoes back from the request (1 = onward, 2 = return). There is no
 * "take whatever came first" fallback: a response carrying only the RETURN solution would otherwise be read
 * as the onward leg and render the return leg's fares under the onward tab. The one positional fallback is
 * a well-formed two-solution response that declares no index at all.
 *
 * Returns null whenever BOTH legs cannot be identified with confidence — callers must then skip the popup
 * (and the benefits overlay) rather than guess.
 */
export const resolveV3LegSolutions = (
    response?: FareFamilyV3Response | null
): { onward: FareFamilyV3OnewaySolution; return: FareFamilyV3OnewaySolution } | null => {
    const solutions = response?.responseBody?.data?.solutions
    if (!Array.isArray(solutions) || solutions.length === 0) return null

    const onward = solutions.find(solution => solution?.index === 1)
    const returnLeg = solutions.find(solution => solution?.index === 2)
    if (onward && returnLeg) return { onward, return: returnLeg }

    // BE omitted `index` on every solution → position is the only signal we have, and only a clean pair
    // is unambiguous.
    if (solutions.length === 2 && solutions.every(solution => solution?.index == null)) {
        return { onward: solutions[0], return: solutions[1] }
    }

    return null
}

/** The base Regular+Regular SPECIAL combo rule in a v3 response, or undefined when there is none. */
export const getRegularSpecialComboRule = (
    response?: FareFamilyV3Response | null
): SpclFareFamilyFareRule | undefined =>
    response?.responseBody?.data?.spclSolution?.[0]?.fareFamilyFareRules?.find(rule =>
        isRegularComboBrand(rule.ctBrandName)
    )

/**
 * Overlay the SPECIAL combo's benefits for `leg` onto this leg's Regular fare.
 *
 * BENEFITS ONLY. Every identifying/pricing key (fareKey, solutionId, comboFbc, totalPrice, basePrice)
 * stays the leg's own, because that is exactly what gets sent when the leg is booked on its own fare —
 * the combo is booked from the combo object itself, never from a leg rule.
 */
const mergeRegularRule = (
    rule: FareFamilyFareRule,
    combo: SpclFareFamilyFareRule,
    leg: SpclLegKey
): FareFamilyFareRule => ({
    ...rule,
    // Anything the combo does not carry for this leg keeps the leg's own value rather than blanking the row.
    cancel: combo.cancel?.[leg] ?? rule.cancel,
    amend: combo.amend?.[leg] ?? rule.amend,
    baggage: combo.baggage?.[leg] ?? rule.baggage,
    seat: combo.seat?.[leg] ?? rule.seat,
    meal: combo.meal?.[leg] ?? rule.meal
})

const applyToSolution = (
    solution: FareFamilyV3OnewaySolution,
    combo: SpclFareFamilyFareRule,
    leg: SpclLegKey
): FareFamilyV3OnewaySolution => ({
    ...solution,
    fareFamilyFareRules: (solution.fareFamilyFareRules ?? []).map(rule =>
        isRegularComboBrand(rule.ctBrandName) ? mergeRegularRule(rule, combo, leg) : rule
    )
})

/**
 * RT split view (v3 fare family): fold the SPECIAL Regular+Regular combo's BENEFITS into the two legs'
 * Regular fares instead of shipping the combo as its own card.
 *
 * The SRP already showed the user the special round-trip price, so the popup must stay consistent with it:
 * each leg's Regular card advertises the combo's benefits for THAT leg while keeping its own fare and its
 * own identifiers. Nothing else in the response is touched — `spclSolution` is preserved verbatim, and the
 * popup uses it for the footer's round-trip total and for the SPECIAL_RT booking payload.
 *
 * KNOWN, ACCEPTED DISCREPANCY: the overlay is unconditional, but the combo is only actually bookable when
 * BOTH legs stay on Regular. Picking a paid fare family on one leg leaves the other leg's Regular card
 * advertising combo benefits it no longer earns — the popup footer calls that out with a note in exactly
 * that mixed case, rather than rebuilding the benefits list per selection.
 *
 * No special combo in the response → returns the response untouched.
 */
export const applySpecialComboToLegFares = (response: FareFamilyV3Response): FareFamilyV3Response => {
    const combo = getRegularSpecialComboRule(response)
    // Same resolver the popup gates on, so a leg can never be overlaid with the OTHER leg's combo benefits.
    const legs = resolveV3LegSolutions(response)
    if (!combo || !legs) return response

    return {
        ...response,
        responseBody: {
            ...response.responseBody,
            data: {
                ...response.responseBody.data,
                solutions: (response.responseBody.data.solutions ?? []).map(solution => {
                    if (solution === legs.onward) return applyToSolution(solution, combo, '1')
                    if (solution === legs.return) return applyToSolution(solution, combo, '2')
                    return solution
                })
            }
        }
    }
}
